import {
  Box,
  FormControl,
  MenuItem,
  Select,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { PimoReactComponent } from "@pimo/pimo-app-builder";
import type { ApexOptions } from "apexcharts";
import React from "react";
import Chart from "react-apexcharts";

import PMBaseCard, { PMBaseCardProps } from "../pm-base-card/pm-base-card";

interface Timespan {
  key: string;
  label: string;
}

type Data = (number | null)[];
type DataWithTimespan = { timespan: string; data: Data }[];
type Categories = (string | number)[];
type CategoriesWithTimespan = { timespan: string; categories: Categories }[];

type SeriesItem = {
  name: string;
  data: (number | null)[];
  color?: string;
};

type Series = SeriesItem[];
type SeriesWithTimespan = { timespan: string; series: Series }[];

export interface PMLineChartCardProps extends Omit<
  PMBaseCardProps,
  "children"
> {
  /** Card title */
  title: string;
  /** Y-axis values (must be same length as `categories`) */
  data?: DataWithTimespan | Data;
  timespans?: Timespan[];
  series?: SeriesWithTimespan | Series;
  /** Series groups shown together in the shared tooltip by category range. */
  tooltipSeriesGroups?: number[][];
  /** X-axis labels (same length as `data`) */
  categories: CategoriesWithTimespan | Categories;
  /** 1 = filled area; 2 = straight line + markers */
  chartType?: 1 | 2;
  /** Label for the X axis */
  xLabel: string;
  /** Label for the Y axis */
  yLabel: string;
  /** One or more stroke/fill color */
  color?: string | string[];
}

/**
 * A line or area chart card powered by ApexCharts.
 *
 * @param {Object} props
 * @param {string} props.title - Card title (also series name).
 * @param {number[]} [props.data] - Array of Y values (used if 'series' is not provided).
 * @param {ApexAxisChartSeries | undefined} [props.series] - Optional ApexCharts series (overrides 'data').
 * @param {(string|number)[]} props.categories - Corresponding X labels.
 * @param {1|2} [props.chartType=1] - 1 = area, 2 = line+markers.
 * @param {string} props.xLabel - X-axis title.
 * @param {string} props.yLabel - Y-axis title.
 * @param {string} [props.color="#2C3D4F"] - Stroke/fill color.
 * @param {React.ReactNode|string} [props.icon="TrendingUp"] - Header icon (MUI icon name or node).
 * @param {boolean} [props.showInfoIcon=false] - Show header info button.
 * @param {boolean} [props.showMenuIcon=false] - Show header menu button.
 * @param {number|string} [props.fixedWidth="100%"] - Card width.
 * @param {number|string} [props.fixedHeight="300px"] - Card height.
 * @returns {JSX.Element}
 */
export const PMLineChartCard: PimoReactComponent<PMLineChartCardProps> = ({
  title,
  data: dataProp = [],
  timespans,
  series: seriesProp,
  tooltipSeriesGroups,
  categories: categoriesProp = [],
  chartType = 1,
  xLabel,
  yLabel,
  color = "#2C3D4F",
  icon = "TrendingUp",
  showInfoIcon = false,
  showMenuIcon = false,
  fixedWidth = "100%",
  fixedHeight = "300px",
  ...rest
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const isArea = chartType === 1;
  const palette = Array.isArray(color) ? color : [color];

  const [selectedTimespan, setSelectedTimespan] =
    React.useState<Timespan | null>(timespans?.[0] ?? null);

  let data: Data = [];
  let categories: Categories = [];
  let series: Series = [];

  if (timespans != null && isDataWithTimespan(dataProp)) {
    const selectedData = dataProp.find(
      (item) => item.timespan === selectedTimespan?.key
    );
    data = selectedData ? selectedData.data : [];
  } else if (isData(dataProp)) {
    data = dataProp;
  }

  if (timespans != null && isCategoriesWithTimespan(categoriesProp)) {
    const selectedCategories = categoriesProp.find(
      (item) => item.timespan === selectedTimespan?.key
    );
    categories = selectedCategories ? selectedCategories.categories : [];
  } else if (isCategories(categoriesProp)) {
    categories = categoriesProp;
  }

  if (timespans != null && isSeriesWithTimespan(seriesProp)) {
    const selectedSeries = seriesProp.find(
      (item) => item.timespan === selectedTimespan?.key
    );
    series = selectedSeries ? selectedSeries.series : [];
  } else if (isSeries(seriesProp)) {
    series = seriesProp;
  }

  const resolvedSeries =
    series.length > 0
      ? series.map(({ name, data: seriesData }) => ({
          name,
          data: seriesData,
        }))
      : [
          {
            name: typeof title === "string" ? title : "Series",
            data,
          },
        ];

  const resolvedColors =
    series.length > 0
      ? series.map((seriesItem, idx) => {
          const fallback = palette[idx] ?? palette[0];
          return seriesItem.color ?? fallback;
        })
      : palette;

  // Responsive sizing for ApexCharts workaround
  const chartHeight = isMobile ? "200px" : isTablet ? "250px" : "100%";

  const chartOptions: ApexOptions = {
    chart: {
      toolbar: { show: false },
      zoom: { enabled: false },
      redrawOnParentResize: true,
      redrawOnWindowResize: true,
      parentHeightOffset: 0,
    },
    colors: resolvedColors,
    legend: {
      show: resolvedSeries.length > 1,
      position: "bottom",
      horizontalAlign: "center",
      floating: false,
      offsetY: 4,
    },
    xaxis: {
      categories,
      tickPlacement: "between",
      title: { text: xLabel },
      labels: {
        rotate: isMobile ? -45 : 0,
        trim: false,
        style: {
          fontSize: isMobile ? "10px" : isTablet ? "11px" : "12px",
        },
      },
    },
    yaxis: {
      title: { text: yLabel },
    },
    stroke: {
      curve: isArea ? "smooth" : "straight",
    },
    markers: {
      size: isArea ? 0 : 5,
      hover: { sizeOffset: 2 },
    },
    fill: {
      type: "solid",
      opacity: isArea ? 0.3 : 1,
    },
    tooltip: {
      enabled: true,
      shared: true,
      intersect: false,
      ...(tooltipSeriesGroups?.length
        ? {
            custom: ({ dataPointIndex, w }) => {
              const categoriesPerGroup = Math.ceil(
                categories.length / tooltipSeriesGroups.length
              );
              const groupIndex = Math.min(
                tooltipSeriesGroups.length - 1,
                Math.floor(dataPointIndex / categoriesPerGroup)
              );
              const seriesIndices = tooltipSeriesGroups[groupIndex] ?? [];
              const rows = seriesIndices
                .map((seriesIndex) => ({
                  name: w.globals.seriesNames[seriesIndex],
                  value: w.globals.series[seriesIndex]?.[dataPointIndex],
                }))
                .filter(({ value }) => value != null)
                .map(
                  ({ name, value }) =>
                    `<div style="display:flex;justify-content:space-between;gap:16px"><span>${name}</span><strong>${value}</strong></div>`
                )
                .join("");

              return `<div style="padding:8px 10px"><div style="font-weight:600;margin-bottom:4px">${categories[dataPointIndex] ?? ""}</div>${rows}</div>`;
            },
          }
        : {}),
    },
    grid: {
      padding: {
        left: 8,
        right: 20,
        top: 8,
        bottom: 0,
      },
    },
  };

  return (
    <PMBaseCard
      icon={icon}
      title={title}
      rightSlotOverride={
        timespans && timespans.length > 0 ? (
          <Box sx={{ mr: 3 }}>
            <FormControl
              variant="standard"
              size="small"
              sx={{
                border: "none !important",
                "&:focus": { border: "none !important" },
                maxWidth: 100,
                ".MuiInputBase-root, .MuiSelect-nativeInput": {
                  "&, &:focus, &:before, &:after": {
                    border: "none !important",
                  },
                  fontSize: "0.625rem",
                },
                ".MuiSelect-select": {
                  paddingBottom: 0,
                },
              }}
            >
              <Select
                label="Timespan"
                value={selectedTimespan?.key || ""}
                onChange={(e) =>
                  setSelectedTimespan(
                    timespans.find((t) => t.key === e.target.value) ?? null
                  )
                }
                size="small"
                sx={{ width: 75 }}
              >
                {timespans.map((item) => (
                  <MenuItem
                    key={item.key}
                    value={item.key}
                    sx={{ fontSize: "0.625rem", lineHeight: "1" }}
                  >
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        ) : undefined
      }
      showInfoIcon={showInfoIcon}
      showMenuIcon={showMenuIcon}
      fixedWidth={fixedWidth}
      fixedHeight={fixedHeight}
      {...rest}
    >
      <Box
        sx={{
          width: "100%",
          height: "100%",
          overflow: "hidden",
          boxSizing: "border-box",
          maxWidth: "100%",
          position: "relative",
          "& .apexcharts-canvas": {
            width: "100% !important",
            maxWidth: "100% !important",
          },
          "& .apexcharts-svg": {
            width: "100% !important",
            maxWidth: "100% !important",
          },
          "& svg": {
            width: "100% !important",
            maxWidth: "100% !important",
          },
          "@media print": {
            overflow: "visible",
            paddingRight: "6px",
          },
        }}
      >
        <Chart
          options={chartOptions}
          series={resolvedSeries}
          type={isArea ? "area" : "line"}
          height={chartHeight}
        />
      </Box>
    </PMBaseCard>
  );
};

function isDataWithTimespan(data: unknown): data is DataWithTimespan {
  return (
    Array.isArray(data) &&
    data.length > 0 &&
    typeof data[0] === "object" &&
    data[0] !== null &&
    "timespan" in data[0] &&
    "data" in data[0]
  );
}

function isData(data: unknown): data is Data {
  return (
    Array.isArray(data) &&
    (data.length === 0 || typeof data[0] === "number" || data[0] === null)
  );
}

function isSeriesWithTimespan(series: unknown): series is SeriesWithTimespan {
  return (
    Array.isArray(series) &&
    series.length > 0 &&
    typeof series[0] === "object" &&
    series[0] !== null &&
    "timespan" in series[0] &&
    "series" in series[0] &&
    Array.isArray(series[0].series)
  );
}

function isSeries(series: unknown): series is Series {
  return (
    Array.isArray(series) &&
    (series.length === 0 ||
      (typeof series[0] === "object" &&
        series[0] !== null &&
        "name" in series[0] &&
        "data" in series[0]))
  );
}

function isCategoriesWithTimespan(
  categories: unknown
): categories is CategoriesWithTimespan {
  return (
    Array.isArray(categories) &&
    categories.length > 0 &&
    typeof categories[0] === "object" &&
    categories[0] !== null &&
    "timespan" in categories[0] &&
    "categories" in categories[0]
  );
}

function isCategories(categories: unknown): categories is Categories {
  return (
    Array.isArray(categories) &&
    (categories.length === 0 ||
      typeof categories[0] === "string" ||
      typeof categories[0] === "number")
  );
}

export default PMLineChartCard;
